/*
WebPT 1.0 Plan tab (Procedures / Modalities / Specialties) as flex++ branches.

The legacy scrape only sees the top-level checkboxes. Each one (P01, M05, SP02…)
reveals a block when checked, and blocks can nest:
  - toggleVisible('<id>')                 -> #div<id>   (or #<id>a01_row for M12a01/02)
  - toggleDosageCustom('<cb>', '<row>')   -> #<row>     (iontophoresis dosage select)
  - toggleModalityCustom('<cb>', '<div>') -> #<div>     (modality Duration select)
  - toggleDoubleIndent('<id>')            -> #<id>a01_row
A block holds sub-option checkboxes, a custom-text textarea and sometimes selects.
Values inside hidden blocks are kept, so we scrape by id and, on fill, click the
checkbox first so WebPT opens the block the same way a user would.
*/

const PLAN_GROUPS: Record<string, string> = {
  P: 'Procedures',
  M: 'Modalities',
  SP: 'Specialties'
};
const FLEX_SENTINELS = new Set(['not discussed', '-99999', '99999']);

type FieldDesc = { el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement };
export type CheckDesc = {
  cb: HTMLInputElement;
  options: Map<string, HTMLInputElement>;
  children: Map<string, CheckDesc>;
  fields: Map<string, FieldDesc>;
};
export type PlanGroups = Map<string, Map<string, CheckDesc>>;

const clean = (s: string) =>
  String(s ?? '').replace(/["']/g, '').replace(/\s+/g, ' ').replace(/:\s*$/, '').trim();

const isBlank = (v: any) =>
  v === null ||
  v === undefined ||
  String(v).trim() === '' ||
  FLEX_SENTINELS.has(String(v).trim().toLowerCase());

function checkboxLabel(cb: HTMLInputElement): string {
  return clean(cb.closest('label')?.textContent || cb.parentElement?.textContent || cb.id);
}

function revealTarget(cb: HTMLInputElement): HTMLElement | null {
  const onclick = cb.getAttribute('onclick') || '';
  const byArg = onclick.match(
    /toggle(?:DosageCustom|ModalityCustom)\('[^']+',\s*'([^']+)'\)/
  );
  if (byArg) return document.getElementById(byArg[1]);
  if (/toggle(?:Visible|DoubleIndent)\('/.test(onclick)) {
    return (
      document.getElementById(`div${cb.id}`) ||
      document.getElementById(`${cb.id}a01_row`)
    );
  }
  return null;
}

function unique(map: Map<string, any>, raw: string): string {
  const base = clean(raw) || 'Details';
  let key = base;
  for (let n = 2; map.has(key); n++) key = `${base} (${n})`;
  return key;
}

function fieldLabel(el: Element, fallback: string): string {
  const row = el.closest('.rowdiv');
  return clean(row?.querySelector('.rowdiv_label')?.textContent || '') || fallback;
}

function describe(cb: HTMLInputElement): CheckDesc {
  const desc: CheckDesc = {
    cb,
    options: new Map(),
    children: new Map(),
    fields: new Map()
  };
  const box = revealTarget(cb);
  if (!box) return desc;
  // Iontophoresis drug rows hold an unlabeled mA/min select.
  const isDosage = /toggleDosageCustom\(/.test(cb.getAttribute('onclick') || '');

  const inner = [...box.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')];
  const nestedBoxes = inner
    .map((c) => revealTarget(c))
    .filter((b): b is HTMLElement => !!b && b !== box);
  const direct = (el: Element) => !nestedBoxes.some((b) => b.contains(el));

  for (const c of inner.filter(direct)) {
    if (revealTarget(c)) desc.children.set(unique(desc.children, checkboxLabel(c)), describe(c));
    else desc.options.set(checkboxLabel(c), c);
  }
  const fields = [
    ...box.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'select, textarea, input[type="text"]'
    )
  ].filter(direct);
  for (const el of fields) {
    const fallback = el.tagName === 'TEXTAREA' ? 'Notes' : isDosage ? 'Dosage' : 'Details';
    desc.fields.set(unique(desc.fields, fieldLabel(el, fallback)), { el });
  }
  return desc;
}

/** Top-level Plan checkboxes grouped as Procedures / Modalities / Specialties. */
export function scrapePlanGroups(planTab: ParentNode): PlanGroups {
  const all = [...planTab.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')];
  const boxes = all.map(revealTarget).filter((b): b is HTMLElement => !!b);
  const groups: PlanGroups = new Map();
  for (const cb of all) {
    const prefix = cb.id.match(/^(SP|P|M)\d+$/)?.[1];
    if (!prefix || boxes.some((b) => b.contains(cb))) continue;
    const name = PLAN_GROUPS[prefix];
    if (!groups.has(name)) groups.set(name, new Map());
    const group = groups.get(name);
    group.set(unique(group, checkboxLabel(cb)), describe(cb));
  }
  return groups;
}

/** Every checkbox label in the groups; the legacy scrape mislabels some as fields. */
export function planCheckboxLabels(groups: PlanGroups): Set<string> {
  const labels = new Set<string>();
  const walk = (d: CheckDesc) => {
    labels.add(checkboxLabel(d.cb));
    d.options.forEach((_, k) => labels.add(k));
    d.children.forEach(walk);
  };
  groups.forEach((g) => g.forEach(walk));
  return labels;
}

function fieldLeaf(el: FieldDesc['el']): Record<string, any> {
  const node: Record<string, any> = {};
  if (el instanceof HTMLSelectElement) {
    const choices = [...new Set([...el.options].map((o) => clean(o.textContent)).filter(Boolean))];
    if (choices.length) node._choices = choices;
    const current = clean(el.selectedOptions[0]?.textContent || '');
    if (current) node._existingValue = current;
  } else if (!isBlank(el.value)) {
    node._existingValue = el.value;
  }
  if (!Object.keys(node).length) node._type = 'string';
  return node;
}

function checkTree(d: CheckDesc): Record<string, any> {
  const tree: Record<string, any> = {
    Selected: { _choices: ['Yes', 'No'], ...(d.cb.checked ? { _existingValue: 'Yes' } : {}) }
  };
  if (d.options.size) {
    const picked = [...d.options].filter(([, c]) => c.checked).map(([k]) => k);
    tree.Options = {
      _choices: [...d.options.keys()],
      _multiple: true,
      _description: 'Every option that applies; empty list if none.',
      ...(picked.length ? { _existingValue: picked.join(', ') } : {})
    };
  }
  d.children.forEach((child, key) => (tree[key] = checkTree(child)));
  d.fields.forEach((f, key) => (tree[key] = fieldLeaf(f.el)));
  return tree;
}

export function planGroupTree(group: Map<string, CheckDesc>, name: string) {
  const tree: Record<string, any> = {
    _description: `${name} planned for this patient. Mark Selected = Yes for each one the visit plans, then fill what opens under it.`
  };
  group.forEach((d, key) => (tree[key] = checkTree(d)));
  return tree;
}

const isYes = (v: any) => /^(yes|true)$/i.test(String(v ?? '').trim());

function hasContent(ans: any): boolean {
  if (Array.isArray(ans)) return ans.some((v) => !isBlank(v));
  if (ans && typeof ans === 'object') {
    return Object.entries(ans).some(([k, v]) =>
      k === 'Selected' ? isYes(v) : !k.startsWith('_') && hasContent(v)
    );
  }
  return !isBlank(ans);
}

function setField(el: FieldDesc['el'], value: any, replyType: string) {
  if (isBlank(value) || typeof value === 'object') return;
  const text = String(value).trim();
  if (el instanceof HTMLSelectElement) {
    const opt = [...el.options].find((o) => clean(o.textContent).toLowerCase() === text.toLowerCase());
    if (!opt || el.value === opt.value) return;
    el.value = opt.value;
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }
  if (el.value === text) return;
  el.value = replyType === 'append' && el.value ? `${el.value}\n${text}` : text;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function fillCheck(d: CheckDesc, ans: any, replyType: string) {
  if (!ans || typeof ans !== 'object' || !hasContent(ans)) return;
  // Click (not .checked) so WebPT's onclick opens the block.
  if (!d.cb.checked) d.cb.click();
  for (const [k, v] of Object.entries(ans)) {
    const key = clean(k);
    if (key === 'Selected' || key.startsWith('_')) continue;
    if (key === 'Options') {
      const picks = Array.isArray(v) ? v : String(v ?? '').split(/\s*[,$]\s*/);
      picks
        .filter((p) => !isBlank(p))
        .forEach((p) => {
          const c = d.options.get(clean(p));
          if (c && !c.checked) c.click();
        });
    } else if (d.children.has(key)) {
      fillCheck(d.children.get(key), v, replyType);
    } else if (d.fields.has(key)) {
      setField(d.fields.get(key).el, v, replyType);
    }
  }
}

/** Fill one group's answer ({ "<item>": { Selected, Options, ... } }). */
export function fillPlanGroup(group: Map<string, CheckDesc>, ans: any, replyType = 'overwrite') {
  if (!ans || typeof ans !== 'object') return;
  for (const [k, v] of Object.entries(ans)) {
    const d = group.get(clean(k));
    if (d) fillCheck(d, v, replyType);
  }
}
